import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { CustomSwitch } from "../../../components/CustomSwitch";

const CoefficientsVersionToggle = ({
  useLatestCoefficients = false,
  onToggle,
  hasNewerCoefficients = false,
  coefficientsVersion = "historical",
  coefficientsDate = null,
  latestCoefficientsDate = null,
  isLoading = false,
}) => {
  const { t, i18n } = useTranslation();

  if (!hasNewerCoefficients && !useLatestCoefficients) {
    return null;
  }

  const isHistorical =
    coefficientsVersion === "historical" || !useLatestCoefficients;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }
      return date.toLocaleDateString(i18n.language || "fr", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const formattedDate = formatDate(coefficientsDate);
  const formattedLatestDate = formatDate(latestCoefficientsDate);

  return (
    <div className="w-full mb-4">
      <div
        className={`rounded-lg p-3 border ${
          isHistorical
            ? "bg-orange-50 border-orange-300"
            : "bg-green-50 border-green-300"
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                isHistorical ? "bg-orange-500" : "bg-green-500"
              }`}
            >
              <i className="fas fa-info text-white text-[10px]" />
            </div>
            <span
              className={`text-xs ${isHistorical ? "text-orange-800" : "text-green-800"}`}
            >
              {isHistorical
                ? `${t("Coefficients from")} ${formattedDate}${formattedLatestDate ? ` — ${t("newer version available")} (${formattedLatestDate})` : ""}`
                : `${t("Up-to-date coefficients")} (${formattedLatestDate})`}
            </span>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <span
              className={`w-24 text-right text-xs font-medium ${
                !useLatestCoefficients ? "text-[#00a7a6]" : "text-gray-400"
              }`}
            >
              {t("Report date")}
            </span>
            <div
              className="relative flex items-center justify-center pt-1"
              style={{ minWidth: 50, minHeight: 26 }}
            >
              {isLoading && (
                <div
                  className="absolute inset-0 flex items-center justify-center rounded z-10"
                  style={{ backgroundColor: "rgba(255,255,255,0.75)" }}
                >
                  <i
                    className="fas fa-spinner fa-spin"
                    style={{ color: "#00a7a6" }}
                  />
                </div>
              )}
              <CustomSwitch
                checked={useLatestCoefficients}
                onChange={(e) => onToggle(e.target.checked)}
                disabled={isLoading}
                switchWidth={50}
                switchHeight={26}
                thumbSize={20}
                aria-label={t(
                  "Toggle between report date and latest coefficients",
                )}
              />
            </div>
            <span
              className={`w-16 text-left text-xs font-medium ${
                useLatestCoefficients ? "text-[#00a7a6]" : "text-gray-400"
              }`}
            >
              {t("Latest")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

CoefficientsVersionToggle.propTypes = {
  useLatestCoefficients: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  hasNewerCoefficients: PropTypes.bool,
  coefficientsVersion: PropTypes.oneOf(["historical", "latest"]),
  coefficientsDate: PropTypes.string,
  latestCoefficientsDate: PropTypes.string,
  isLoading: PropTypes.bool,
};

export default CoefficientsVersionToggle;
